/** Presentation helpers. Every amount that arrives from the API is integer cents. */

export function money(cents: number | null | undefined, currency = 'MYR'): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return `${currency} —`;
  const negative = cents < 0;
  const absolute = Math.abs(cents);
  const major = Math.floor(absolute / 100).toLocaleString('en-US');
  const minor = String(absolute % 100).padStart(2, '0');
  return `${negative ? '−' : ''}${currency} ${major}.${minor}`;
}

/** Same as `money` but always carries an explicit sign — for ledger rows. */
export function signedMoney(cents: number, currency = 'MYR'): string {
  const sign = cents > 0 ? '+' : cents < 0 ? '−' : '';
  const absolute = Math.abs(cents);
  const major = Math.floor(absolute / 100).toLocaleString('en-US');
  const minor = String(absolute % 100).padStart(2, '0');
  return `${sign}${currency} ${major}.${minor}`;
}

/** Parses a typed major-unit amount ("3,922.40") into integer cents. */
export function parseMoney(input: string): number | null {
  const cleaned = input.replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const value = Number(cleaned);
  if (Number.isNaN(value)) return null;
  return Math.round(value * 100);
}

export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Date and clock, to the second.
 *
 * Seconds are not decoration here: rounds close five minutes apart and results
 * land on an exact tick, so "17 Sep 2026, 09:30" for two different rounds is the
 * one thing an operator reconciling a result against a bet cannot work with.
 */
export function dateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * An ISO timestamp as a `datetime-local` input wants it: `YYYY-MM-DDTHH:mm`, in
 * the viewer's own zone.
 *
 * Built from the local parts rather than by slicing `toISOString()`, which is
 * UTC — an operator in UTC+8 would open the field on a time eight hours off the
 * one the table beside it is showing, and "correct" it back to where it started.
 */
export function toLocalInput(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** The reverse: what the input holds, as an ISO instant. Null if unparseable. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function timeOnly(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Human-readable offset from now, in either direction. A round that has not
 * closed yet is in the future, so this has to read forwards as well as back —
 * otherwise an open round shows "-96s ago".
 */
export function relative(value: string | null | undefined): string {
  if (!value) return '—';
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  const future = seconds < 0;
  const magnitude = Math.abs(seconds);

  const amount =
    magnitude < 60
      ? `${magnitude}s`
      : magnitude < 3600
        ? `${Math.floor(magnitude / 60)}m`
        : magnitude < 86_400
          ? `${Math.floor(magnitude / 3600)}h`
          : `${Math.floor(magnitude / 86_400)}d`;

  if (magnitude < 5) return 'just now';
  return future ? `in ${amount}` : `${amount} ago`;
}

export function countdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function duration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function odds(basisPoints: number): string {
  return `${(basisPoints / 10_000).toFixed(2)}×`;
}

/** "user.balance_set" → "Balance set" */
export function humanAction(action: string): string {
  const tail = action.split('.').slice(1).join(' ') || action;
  const words = tail.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function titleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
